import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CommentBox } from '@components/Summary/CommentBox';
import { ServicesDeliveredBy } from '@components/Summary/ServicesDeliveredBy';
import { AcceptTireOfferButton } from '@components/TireOffer/AcceptTireOfferButton';
import { PaymentOptionsInstructions } from '@components/TireOffer/PaymentOptionsInstructions';
import { RedirectToStatusOrShowOfferStepWrapper } from '@components/TireOffer/RedirectToStatusOrShowOfferStepWrapper';
import { TireOfferAddressCard } from '@components/TireOffer/TireOfferAddressCard';
import { TireOfferNextInstructions } from '@components/TireOffer/TireOfferNextInstructions';
import { TireOfferPaymentSummary } from '@components/TireOffer/TireOfferPaymentSummary';
import { useTireOfferDeliveryCheck } from '@components/TireOffer/useTireOfferDeliveryCheck';
import { t } from '@lingui/core/macro';
import { PaymentOption, TireQuoteRecordCustomer } from '@navio/nsdk';
import { useIsFetching } from '@tanstack/react-query';
import { URLKeys } from 'noddi-async';
import { NoddiCard } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { useTireOfferStore } from '../../../stores/tireOfferStore';

export const TireOfferSummary = () => {
  const { isLoggedIn } = useAuthContext();

  return (
    <RedirectToStatusOrShowOfferStepWrapper>
      {(data) => {
        // Login is its own step, so this page is review-and-pay only.
        if (!isLoggedIn) {
          return <Navigate to={customerRoutes.tireOfferLogin.getPath({ slug: data.slug })} replace />;
        }
        return <SummaryContent tireOffer={data} />;
      }}
    </RedirectToStatusOrShowOfferStepWrapper>
  );
};

const SummaryContent = ({ tireOffer }: { tireOffer: TireQuoteRecordCustomer }) => {
  const { selectedQuoteItem, selectedAddress } = useTireOfferStore();
  const [userComment, setUserComment] = useState('');
  const { currentBrand } = useCurrentBrand();
  // Login refetches the offer: only the logged-in read carries the saved address.
  const isOfferRefetching =
    useIsFetching({ queryKey: [URLKeys.getCarTireQuoteBySlug, { carTireQuoteSlug: tireOffer.slug }] }) > 0;

  // Tire hotel customers get the tires fitted on their stored wheel set, so there is no visit to place.
  const needsAddress = !tireOffer.hasActiveTireHotelContractForWheelSet;
  // A pick on the address step wins; otherwise the address we already know, so the customer is not asked again.
  const address = needsAddress ? (selectedAddress ?? tireOffer.suggestedAddress ?? undefined) : undefined;
  const {
    data: deliveryCheck,
    isPending: isDeliveryCheckPending,
    error: deliveryCheckError
  } = useTireOfferDeliveryCheck({ tireOffer, addressId: address?.id });

  if (needsAddress && !address) {
    if (isOfferRefetching) {
      return <NoddiLoadingScreenWithTrans />;
    }
    return <Navigate to={customerRoutes.tireOfferAddress.getPath({ slug: tireOffer.slug })} replace />;
  }

  const { brand } = tireOffer.serviceDepartment;
  return (
    // Back sits in the confirm bar, which only renders with a picked option.
    <ContentWrapperCustomerApp title={t`Ready to confirm your order?`} hideBackButton={!!selectedQuoteItem}>
      <div className='flex flex-col gap-4'>
        <NoddiCard title={t`Your order`}>
          <TireOfferPaymentSummary
            quoteItemTires={selectedQuoteItem?.quoteItemTires ?? []}
            genericItems={tireOffer.genericItems}
            isStaggered={!!selectedQuoteItem?.isStaggered}
          />
          {/* Same rule as the booking summary: only name the provider when it isn't the brand the customer is on */}
          {brand.domain !== currentBrand && <ServicesDeliveredBy brandName={brand.name} logoUrl={brand.logo.url} />}
        </NoddiCard>
        {address && (
          <TireOfferAddressCard
            slug={tireOffer.slug}
            address={address}
            deliversToAddress={deliveryCheck?.deliversToAddress}
          />
        )}
        {deliveryCheckError && <ApiErrorMessageWithTrans error={deliveryCheckError} />}
        <TireOfferNextInstructions
          hasActiveTireHotelContractForWheelSet={tireOffer.hasActiveTireHotelContractForWheelSet}
        />
        {tireOffer.paymentMethod === PaymentOption.PAY_BEFORE && <PaymentOptionsInstructions />}

        <CommentBox
          comment={userComment}
          handleChange={setUserComment}
          placeholder={t`Optional note if any extra information is needed...`}
        />
        <AcceptTireOfferButton
          userComment={userComment}
          slug={tireOffer.slug}
          paymentMethod={tireOffer.paymentMethod}
          addressId={address?.id}
          // An address we do not reach cannot be confirmed. A failed check does not block: it never did on the address step either.
          disabled={!!address && (isDeliveryCheckPending || deliveryCheck?.deliversToAddress === false)}
        />
      </div>
    </ContentWrapperCustomerApp>
  );
};
